import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "layout__wrapper-inner container" }

import { onBeforeMount, ref, Raw, watch } from 'vue'
import { useRouter } from 'vue-router'
import RootPageLayout from '@/app/components/RootPageLayout.vue'
import MultiDocsBlock20321 from '@/app/components/listing/MultiDocsBlock20321.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import CustomListForm from '@/app/components/custom.lists/CustomListForm.vue'
import type {
  CustomList,
  CustomListWithDocs,
  Doc,
  DocsUserDataResponse,
} from '@/models/interfaces'
import {
  createDocsView,
  DocSortOption,
  DocsView,
  DocsViewMediator,
} from '@/models/doc'
import { ListView } from '@/models/docs.list'
import { CreateCustomListForm, CustomListsView } from '@/models/custom.lists'

type CustomListEntry = {
  list: CustomList
  docsView: Raw<DocsView>
  listView: Raw<ListView>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LibraryHome20321',
  props: {
    docs: {},
    docsUserData: {},
    lists: {}
  },
  setup(__props: any) {

/**
 * The replacement Library component for exp_save_for_later_20321.
 * This is rendered by MyLibrary.vue in place of the usual tabs.
 */
const router = useRouter()

const props = __props

const wantToReadView = ref<Raw<ListView> | null>(null)
const favoritesView = ref<Raw<ListView> | null>(null)
const finishedView = ref<Raw<ListView> | null>(null)
const nowReadingView = ref<Raw<ListView> | null>(null)
const offlineBooksView = ref<Raw<ListView> | null>(null)
const downloadsView = ref<Raw<ListView> | null>(null)

// This view is passed to the Rename/Delete widget so that they can
// modify the prop.lists.
const customListsView = ref<Raw<CustomListsView>>(
  new CustomListsView(props.lists),
)

// Popup for creating a new custom list.
const customListForm = ref<InstanceType<typeof CustomListForm> | null>(null)
const createListFormView = ref<Raw<CreateCustomListForm>>(
  new CreateCustomListForm(undefined, customListsView.value, false, router),
)

// List of blocks whose ids and url_slugs never change.
const staticDocsViews = ref<Raw<DocsView>[]>([])

// List of blocks representing CustomLists, which may change (if a list
// is deleted or renamed).
const customListViews = ref<CustomListEntry[]>([])

watch(
  () => props.lists,
  async (lists) => {
    customListsView.value = new CustomListsView(lists)
    await recomputeCustomListViews(lists)
  },
  { deep: true },
)

onBeforeMount(async () => {
  // "Want to Read" is a special custom list with an immutable url_slug.
  // Search for it.
  let wantToReadList: DocsView | undefined
  const index = props.lists.findIndex(
    (list) => list.is_standard && list.url_slug === 'want-to-read',
  )
  if (index >= 0) {
    const list = props.lists[index]

    wantToReadList = await createDocsView(
      props.docs,
      props.docsUserData,
      DocSortOption.viewed,
    )
    await wantToReadList.setCustomList(list.id)
    wantToReadList.enableActionsButton()
    wantToReadView.value = new ListView('Want to Read', wantToReadList, {
      customCardComponent: 'AnyDocCard',
      viewAllLink: `/app/library/list/want-to-read`,
    })
  }

  const favorites = await createDocsView(
    props.docs,
    props.docsUserData,
    DocSortOption.title,
  )
  favorites.enableActionsButton()
  await favorites.setIsFavorite(true)
  favoritesView.value = new ListView('Favorites', favorites, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/favorites',
  })

  const finished = await createDocsView(
    props.docs,
    props.docsUserData,
    DocSortOption.title,
  )
  finished.enableActionsButton()
  await finished.setList('finished')
  finishedView.value = new ListView('Finished', finished, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/finished',
  })

  const readingNow = await createDocsView(
    props.docs,
    props.docsUserData,
    DocSortOption.viewed,
  )
  await readingNow.setList('reading')
  await readingNow.setOnlyInProgress(false)
  readingNow.enableContinueReading()
  readingNow.enableActionsButton()
  nowReadingView.value = new ListView('Reading Now', readingNow, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/reading',
  })

  const offlineBooks = await createDocsView(
    props.docs,
    props.docsUserData,
    undefined, // Default defaultSort value
  )
  offlineBooks.enableActionsButton()
  await offlineBooks.filterByOffline()
  offlineBooksView.value = new ListView('Offline Books', offlineBooks, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/offline',
  })

  const downloads = await createDocsView(
    props.docs,
    props.docsUserData,
    DocSortOption.viewed,
  )
  downloads.enableActionsButton()
  await downloads.filterByExports()
  downloadsView.value = new ListView('Download History', downloads, {
    customCardComponent: 'AnyDocCard',
    viewAllLink: '/app/library/downloads',
  })

  // These DocsViews are always present and never change. (Their contents
  // may change, but not the views themselves.)
  const staticViews = [favorites, readingNow, finished, offlineBooks, downloads]

  // This one may or may not be present, but if it is, it won't change.
  if (wantToReadList) {
    staticViews.push(wantToReadList)
  }

  staticDocsViews.value = staticViews

  // Create the views for the custom lists. This is based on props.lists,
  // which may change if lists are renamed or deleted.
  await recomputeCustomListViews(props.lists)
})

/**
 * Create the list of custom-list views which will be displayed after the
 * static views. This is called at mount, and whenever props.lists changes.
 */
async function recomputeCustomListViews(
  lists: CustomListWithDocs[],
): Promise<void> {
  customListViews.value.length = 0

  for (const list of lists) {
    if (!list.is_standard) {
      // This excludes "want-to-read", which has already been handled above.
      //
      // Note that we're not using the contents of prop.lists at all
      // here. The custom-list block is based on prop.docs, same as
      // the rest of the page. We're just filtering on the custom list
      // name.
      // If we make 20321 permanent, we could simplify MyLibrary.vue to
      // only fetch the names of the custom lists rather than their
      // contents.
      const onList = await createDocsView(
        props.docs,
        props.docsUserData,
        DocSortOption.viewed,
      )
      await onList.setCustomList(list.id)
      onList.enableActionsButton()
      customListViews.value.push({
        list: list,
        docsView: onList,
        listView: new ListView(list.title, onList, {
          customCardComponent: 'AnyDocCard',
          viewAllLink: `/app/library/list/${list.url_slug}`,
        }),
      })
    }
  }

  // The list of doc-list views on this page. They all share the same data
  // (props.docs). This combines the permanent (static) lists with the
  // dynamic list of custom lists.
  //
  // We create this but we don't need to store it anywhere; the views
  // hold references to it.
  //
  // When recalculating, this replaces the previous DocsViewMediator.
  new DocsViewMediator([
    ...staticDocsViews.value,
    ...customListViews.value.map((obj) => obj.docsView),
  ])
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(RootPageLayout, {
    class: "library-page",
    title: "My Library"
  }, {
    actions: _withCtx(() => [
      _createVNode(AppButton, {
        class: "library-page__new-list-btn",
        icon: "iadd",
        onClick: customListForm.value?.show
      }, {
        default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
          _createElementVNode("span", { class: "library-page__new-list-label library-page__new-list-label--desktop" }, " New Reading List ", -1),
          _createElementVNode("span", { class: "library-page__new-list-label library-page__new-list-label--mobile" }, " New List ", -1)
        ]))]),
        _: 1
      }, 8, ["onClick"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(CustomListForm, {
          ref_key: "customListForm",
          ref: customListForm,
          class: "page-top__new-list-form",
          view: createListFormView.value
        }, null, 8, ["view"]),
        (wantToReadView.value && wantToReadView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 0,
              view: wantToReadView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (favoritesView.value && favoritesView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 1,
              view: favoritesView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (nowReadingView.value && nowReadingView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 2,
              view: nowReadingView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (finishedView.value && finishedView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 3,
              view: finishedView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (offlineBooksView.value && offlineBooksView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 4,
              view: offlineBooksView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (downloadsView.value && downloadsView.value.dataLength)
          ? (_openBlock(), _createBlock(MultiDocsBlock20321, {
              key: 5,
              view: downloadsView.value
            }, null, 8, ["view"]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customListViews.value, (entry) => {
          return (_openBlock(), _createBlock(MultiDocsBlock20321, {
            key: entry.list.id,
            customList: entry.list,
            customListsView: customListsView.value,
            view: entry.listView
          }, null, 8, ["customList", "customListsView", "view"]))
        }), 128))
      ])
    ]),
    _: 1
  }))
}
}

})